import type { FallbackRender } from "@sentry/react";
import { Component, type ReactNode } from "react";
import { captureError } from "./sentry";

interface Props {
  fallback: FallbackRender;
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Drop-in replacement for `Sentry.ErrorBoundary` that does not need Sentry to
 * be loaded (QW4 in specs/bundle-optimization.md). It renders the same
 * fallbacks and reports to Sentry through the deferred boot helper: errors
 * thrown before Sentry boots are queued and replayed once it loads, so nothing
 * is lost on pages like the login screen where Sentry arrives late.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, { componentStack }: { componentStack: string | null }) {
    this.setState({ componentStack });
    captureError(error);
  }

  resetError = () => {
    this.setState({ error: null, componentStack: null });
  };

  render() {
    const { error, componentStack } = this.state;
    if (error !== null) {
      // Sentry's FallbackRender shape — kept for drop-in compatibility with
      // the existing fallback components.
      return this.props.fallback({
        error,
        componentStack: componentStack ?? "",
        eventId: "",
        resetError: this.resetError,
      });
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
